// Meta Pixel (Facebook Pixel) Service
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

export class MetaPixelService {
  private static pixelIds = ['708059561818799', '1654433431756957'];
  private static isInitialized = false;

  static init(): void {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }

    // Load Facebook Pixel script
    (function(f: any, b: Document, e: string, v: string, n: any, t: HTMLScriptElement | null, s: HTMLScriptElement | null) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e) as HTMLScriptElement;
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0] as HTMLScriptElement;
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      }
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', undefined, null, null);

    // Initialize all pixels
    this.pixelIds.forEach(pixelId => {
      window.fbq('init', pixelId);
      console.log('Meta Pixel initialized with ID:', pixelId);
    });
    
    // Track initial page view for all pixels
    window.fbq('track', 'PageView');

    // Add noscript fallback images
    this.addNoscriptFallback();

    this.isInitialized = true;
    console.log('All Meta Pixels initialized successfully');
  }

  private static addNoscriptFallback(): void {
    // Create noscript fallback images for users with JavaScript disabled
    this.pixelIds.forEach(pixelId => {
      const noscript = document.createElement('noscript');
      const img = document.createElement('img');
      img.height = 1;
      img.width = 1;
      img.style.display = 'none';
      img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
      noscript.appendChild(img);
      document.head.appendChild(noscript);
    });
  }

  /** eventId lets Meta deduplicate this against the same event sent later from the server (Conversions API). */
  static trackEvent(eventName: string, parameters?: Record<string, any>, eventId?: string): void {
    if (!this.isInitialized || typeof window === 'undefined' || !window.fbq) {
      console.warn('Meta Pixel not initialized. Call MetaPixelService.init() first.');
      return;
    }

    try {
      if (eventId) {
        window.fbq('track', eventName, parameters || {}, { eventID: eventId });
      } else if (parameters) {
        window.fbq('track', eventName, parameters);
      } else {
        window.fbq('track', eventName);
      }
      console.log('Meta Pixel event tracked:', eventName, parameters);
    } catch (error) {
      console.error('Error tracking Meta Pixel event:', error);
    }
  }

  static trackPageView(): void {
    this.trackEvent('PageView');
  }

  static trackCustomEvent(eventName: string, parameters?: Record<string, any>): void {
    if (!this.isInitialized || typeof window === 'undefined' || !window.fbq) {
      console.warn('Meta Pixel not initialized. Call MetaPixelService.init() first.');
      return;
    }

    try {
      if (parameters) {
        window.fbq('trackCustom', eventName, parameters);
      } else {
        window.fbq('trackCustom', eventName);
      }
      console.log('Meta Pixel custom event tracked:', eventName, parameters);
    } catch (error) {
      console.error('Error tracking Meta Pixel custom event:', error);
    }
  }

  // Common e-commerce events
  static trackPurchase(value: number | undefined, currency: string = 'AUD', eventId?: string): void {
    this.trackEvent('Purchase', typeof value === 'number' ? { value, currency } : { currency }, eventId);
  }

  static trackLead(): void {
    this.trackEvent('Lead');
  }

  static trackCompleteRegistration(): void {
    this.trackEvent('CompleteRegistration');
  }

  static trackViewContent(contentName?: string, contentCategory?: string): void {
    const parameters: Record<string, any> = {};
    if (contentName) parameters.content_name = contentName;
    if (contentCategory) parameters.content_category = contentCategory;
    
    this.trackEvent('ViewContent', Object.keys(parameters).length > 0 ? parameters : undefined);
  }

  static trackSearch(searchString: string): void {
    this.trackEvent('Search', { search_string: searchString });
  }
}

export default MetaPixelService;
